import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import RefinanceEligibilityForm, { RefinanceEligibilityFormData } from '../components/RefinanceEligibilityForm';
import './RefinanceHome.css';

const RefinanceHome: React.FC = () => {
  const [openFAQ, setOpenFAQ] = useState<number | null>(0);
  const [showEligibilityForm, setShowEligibilityForm] = useState(false);
  const [statsBackgroundImage, setStatsBackgroundImage] = useState<string>('');
  const [heroImage, setHeroImage] = useState<string>('');
  const [whyChooseUsImage, setWhyChooseUsImage] = useState<string>('');
  const [qualifyImage, setQualifyImage] = useState<string>('');
  const [bestTimeImage, setBestTimeImage] = useState<string>('');
  const [contactAvatarImage, setContactAvatarImage] = useState<string>('');
  
  const defaultStatsImage = 'https://api.builder.io/api/v1/image/assets/TEMP/46b7939e5bebf1b51c9bf6b1599f55e6c8e8dbea?width=2880';
  const defaultPlaceholder = 'https://api.builder.io/api/v1/image/assets/TEMP/placeholder';

  useEffect(() => {
    // Load stats background image
    const statsImg = new Image();
    statsImg.src = '/assets/refinance-home/stats/cta.jpg';
    statsImg.onload = () => setStatsBackgroundImage(`url(${statsImg.src})`);
    statsImg.onerror = () => setStatsBackgroundImage(`url(${defaultStatsImage})`);

    // Load hero image
    const heroImg = new Image();
    heroImg.src = '/assets/refinance-home/hero/refinance.png';
    heroImg.onload = () => setHeroImage(heroImg.src);
    heroImg.onerror = () => setHeroImage(defaultPlaceholder);

    // Load why choose us image
    const whyImg = new Image();
    whyImg.src = '/assets/refinance-home/why-choose-us/house.jpg';
    whyImg.onload = () => setWhyChooseUsImage(whyImg.src);
    whyImg.onerror = () => setWhyChooseUsImage(defaultPlaceholder);

    // Load qualify image
    const qualifyImg = new Image();
    qualifyImg.src = '/assets/refinance-home/qualify/house.jpg';
    qualifyImg.onload = () => setQualifyImage(qualifyImg.src);
    qualifyImg.onerror = () => setQualifyImage(defaultPlaceholder);

    // Load best time image
    const bestTimeImg = new Image();
    bestTimeImg.src = '/assets/refinance-home/best-time/house.jpg';
    bestTimeImg.onload = () => setBestTimeImage(bestTimeImg.src);
    bestTimeImg.onerror = () => setBestTimeImage(defaultPlaceholder);

    // Load contact avatar image
    const contactImg = new Image();
    contactImg.src = '/assets/refinance-home/faq-contact/contact.jpg';
    contactImg.onload = () => setContactAvatarImage(contactImg.src);
    contactImg.onerror = () => {
      const altImg = new Image();
      altImg.src = '/assets/refinance-home/faq-contact/avatar.jpg';
      altImg.onload = () => setContactAvatarImage(altImg.src);
      altImg.onerror = () => setContactAvatarImage(defaultPlaceholder);
    };
  }, []);

  const toggleFAQ = (index: number) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  const handleEligibilityFormSubmit = (formData: RefinanceEligibilityFormData) => {
    // TODO: Implement form submission logic (e.g., send to API)
    console.log('Refinance Form submitted with data:', formData);
    // Don't close the form here - let the user see step 8 (thank you page)
    // The form will close when user clicks "Close" button on step 8
  };

  const faqs = [
    {
      question: 'How much can I save by refinancing?',
      answer: 'The amount you can save depends on your current mortgage, interest rate, and the terms of your new loan. Our team can help you calculate potential savings based on your situation.'
    },
    {
      question: 'What are the costs associated with refinancing?',
      answer: 'Refinancing typically involves closing costs, which can include appraisal fees, title fees, and other lender charges. We\'ll help you understand the costs and determine if refinancing is right for you.'
    },
    {
      question: 'How do I qualify for a mortgage?',
      answer: 'On average, refinancing takes 30-45 days, depending on your situation. We work efficiently to ensure a smooth and timely process.'
    },
    {
      question: 'What credit score do I need to qualify for a mortgage?',
      answer: 'Yes, it\'s possible to refinance with bad credit, but the terms may vary. We\'ll help you explore your options and find the best solution.'
    },
    {
      question: 'How long does the mortgage approval process usually take?',
      answer: 'Refinancing is a good option when interest rates are lower than your current rate or if you want to access your home\'s equity. We\'ll assess your situation to see if now is the right time for you.'
    }
  ];

  return (
    <div className="refinance-home-page">
      <Navbar />
      <main>
        {/* Hero Section */}
        <section className="refinance-hero">
          <div className="refinance-container">
            <div className="refinance-hero-content">
              <div className="refinance-hero-text">
                <h1 className="refinance-hero-title">
                  Save more with the right Refinance Plan
                </h1>
                <p className="refinance-hero-description">
                  Lower your monthly payments, access equity, or shorten your loan term with our tailored refinancing solutions.
                </p>
                <div className="refinance-hero-ctas">
                  <button 
                    className="refinance-cta-primary"
                    onClick={() => setShowEligibilityForm(true)}
                  >
                    Check your eligibility
                  </button>
                  <button className="refinance-cta-secondary">Contact us</button>
                </div>
                <p className="refinance-trust-line">
                  Join <strong>hundreds of homeowners</strong> who've saved thousands by refinancing with us. Rated <strong>4.9/5</strong> by satisfied clients.
                </p>
              </div>
              <div className="refinance-hero-image">
                <img 
                  src={heroImage || defaultPlaceholder} 
                  alt="Refinance home" 
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = defaultPlaceholder;
                  }}
                />
              </div>
            </div>
          </div>
        </section>

        {/* Partner Logos Section */}
        <section className="refinance-partners">
          <div className="refinance-container">
            <div className="refinance-partners-grid">
              <div className="refinance-partner-card">
                <img src="https://api.builder.io/api/v1/image/assets/TEMP/548487c4b41fbe61af6e61e31aff9a2149089bfb?width=608" alt="Sierra Pacific Mortgage" />
              </div>
              <div className="refinance-partner-card">
                <img src="https://api.builder.io/api/v1/image/assets/TEMP/6e0dd72837563fabb3a036993de374e709a69fb2?width=527" alt="AmiVest" />
              </div>
              <div className="refinance-partner-card">
                <img src="https://api.builder.io/api/v1/image/assets/TEMP/1b42400f0879d4e23c30a78eea62d6e536563b76?width=567" alt="Advancial" />
              </div>
            </div>
          </div>
        </section>

        {/* 4-Step Refinancing Process Section */}
        <section className="refinance-process">
          <div className="refinance-container">
            <div className="refinance-process-header">
              <h2 className="refinance-section-title">Our 4-Step Refinancing Process</h2>
              <p className="refinance-section-description">
                Refinancing is easy with our simple, step-by-step process. We handle the details, guiding you from reviewing your mortgage to securing better rates or accessing equity, all while helping you save and meet your financial goals.
              </p>
            </div>
            <div className="refinance-steps-grid">
              <div className="refinance-step-card">
                <div className="refinance-step-icon">
                  <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M24 4L30 18L44 20L32 30L34 44L24 36L14 44L16 30L4 20L18 18L24 4Z" fill="#0578EC"/>
                  </svg>
                </div>
                <h3 className="refinance-step-title">Check eligibility</h3>
                <p className="refinance-step-description">
                  We review your current mortgage and financial situation to see if refinancing makes sense for you.
                </p>
              </div>
              <div className="refinance-step-card">
                <div className="refinance-step-icon">
                  <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M8 24L20 12L28 20L40 8" stroke="#0578EC" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M28 8H40V20" stroke="#0578EC" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <h3 className="refinance-step-title">Compare loan options</h3>
                <p className="refinance-step-description">
                  We'll present the best refinance options available, from lowering your rate to accessing equity.
                </p>
              </div>
              <div className="refinance-step-card">
                <div className="refinance-step-icon">
                  <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M24 4C12.954 4 4 12.954 4 24C4 35.046 12.954 44 24 44C35.046 44 44 35.046 44 24C44 12.954 35.046 4 24 4Z" stroke="#0578EC" strokeWidth="2"/>
                    <path d="M24 16V24L28 28" stroke="#0578EC" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </div>
                <h3 className="refinance-step-title">Apply for refinancing</h3>
                <p className="refinance-step-description">
                  Our team guides you through the application process for a quick and seamless experience.
                </p>
              </div>
              <div className="refinance-step-card">
                <div className="refinance-step-icon">
                  <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M8 24L20 12L28 20L40 8" stroke="#0578EC" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <h3 className="refinance-step-title">Finalize your Refinance</h3>
                <p className="refinance-step-description">
                  We ensure a smooth closing process, helping you to take advantage of your new loan terms as soon as possible.
                </p>
              </div>
            </div>
            <div className="refinance-process-cta">
              <button className="refinance-cta-primary">Get started today</button>
            </div>
          </div>
        </section>

        {/* Refinance with confidence section */}
        <section className="refinance-why">
          <div className="refinance-container">
            <div className="refinance-why-content">
              <div className="refinance-why-text">
                <h2 className="refinance-section-title">Refinance with confidence and save more</h2>
                <div className="refinance-benefits-list">
                  <div className="refinance-benefit-item">
                    <div className="refinance-star-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="#0578EC"/>
                      </svg>
                    </div>
                    <p>
                      <strong>Lower Monthly Payments:</strong>{' '}
                      Reduce your mortgage payments and free up more of your income.
                    </p>
                  </div>
                  <div className="refinance-benefit-item">
                    <div className="refinance-star-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="#0578EC"/>
                      </svg>
                    </div>
                    <p>
                      <strong>Access Home Equity:</strong>{' '}
                      Tap into your home's value for renovations, debt consolidation, or investments.
                    </p>
                  </div>
                  <div className="refinance-benefit-item">
                    <div className="refinance-star-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="#0578EC"/>
                      </svg>
                    </div>
                    <p>
                      <strong>Better Interest Rates:</strong>{' '}
                      We help you find lower interest rates, saving you money over time.
                    </p>
                  </div>
                  <div className="refinance-benefit-item">
                    <div className="refinance-star-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="#0578EC"/>
                      </svg>
                    </div>
                    <p>
                      <strong>Flexible Loan Terms:</strong>{' '}
                      Adjust your loan term to fit your financial goals—whether it's paying off faster or extending for lower payments.
                    </p>
                  </div>
                </div>
                <div className="refinance-why-ctas">
                  <button className="refinance-cta-primary">Get your rate</button>
                  <button className="refinance-cta-secondary">
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                      <path d="M6.28521 6.64001C6.80721 7.72723 7.5188 8.74621 8.41998 9.64739C9.32116 10.5486 10.3401 11.2602 11.4274 11.7822C11.5209 11.8271 11.5676 11.8495 11.6268 11.8668C11.8371 11.9281 12.0953 11.884 12.2733 11.7565C12.3234 11.7206 12.3663 11.6778 12.452 11.592C12.7143 11.3298 12.8454 11.1987 12.9772 11.113C13.4744 10.7898 14.1153 10.7898 14.6125 11.113C14.7443 11.1987 14.8754 11.3298 15.1376 11.592L15.2838 11.7382C15.6823 12.1368 15.8816 12.3361 15.9899 12.5501C16.2052 12.9758 16.2052 13.4785 15.9899 13.9041C15.8816 14.1182 15.6823 14.3175 15.2838 14.7161L15.1655 14.8343C14.7683 15.2315 14.5697 15.4301 14.2997 15.5818C14 15.7501 13.5347 15.8711 13.191 15.8701C12.8813 15.8692 12.6696 15.8091 12.2463 15.6889C9.97125 15.0432 7.82448 13.8249 6.0335 12.0339C4.24251 10.2429 3.02415 8.09612 2.37843 5.82108C2.25827 5.39774 2.19819 5.18607 2.19727 4.87636C2.19625 4.5327 2.31727 4.06733 2.48559 3.7677C2.63727 3.49768 2.83588 3.29907 3.2331 2.90185L3.35132 2.78362C3.74991 2.38504 3.9492 2.18575 4.16323 2.07749C4.5889 1.86219 5.0916 1.86219 5.51727 2.07749C5.73131 2.18575 5.9306 2.38504 6.32918 2.78362L6.47533 2.92978C6.73754 3.19198 6.86864 3.32308 6.95436 3.45492C7.2776 3.95208 7.2776 4.59302 6.95435 5.09019C6.86864 5.22202 6.73754 5.35312 6.47533 5.61533C6.3896 5.70106 6.34673 5.74393 6.31085 5.79404C6.18334 5.97211 6.13932 6.2303 6.20061 6.44057C6.21786 6.49974 6.24031 6.5465 6.28521 6.64001Z" stroke="black" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Contact us
                  </button>
                </div>
              </div>
              <div className="refinance-why-image">
                <img 
                  src={whyChooseUsImage || defaultPlaceholder} 
                  alt="Why choose us" 
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = defaultPlaceholder;
                  }}
                />
              </div>
            </div>
          </div>
        </section>

        {/* Are you qualify section */}
        <section className="refinance-qualify">
          <div className="refinance-container">
            <div className="refinance-qualify-content">
              <div className="refinance-qualify-image">
                <img 
                  src={qualifyImage || defaultPlaceholder} 
                  alt="Qualify for refinancing" 
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = defaultPlaceholder;
                  }}
                />
              </div>
              <div className="refinance-qualify-text">
                <h2 className="refinance-section-title">Are you qualify for home refinancing?</h2>
                <div className="refinance-benefits-list">
                  <div className="refinance-benefit-item">
                    <div className="refinance-star-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="#0578EC"/>
                      </svg>
                    </div>
                    <p>
                      <strong>Credit Score:</strong>{' '}
                      Typically, a score of 620 or higher is required, but options are available for various credit profiles.
                    </p>
                  </div>
                  <div className="refinance-benefit-item">
                    <div className="refinance-star-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="#0578EC"/>
                      </svg>
                    </div>
                    <p>
                      <strong>Equity in Your Home:</strong>{' '}
                      The more equity you have, the better your chances of qualifying for a lower rate or cash-out refinancing.
                    </p>
                  </div>
                  <div className="refinance-benefit-item">
                    <div className="refinance-star-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="#0578EC"/>
                      </svg>
                    </div>
                    <p>
                      <strong>Stable Income:</strong>{' '}
                      Lenders will review your income to ensure you can handle the new loan terms.
                    </p>
                  </div>
                  <div className="refinance-benefit-item">
                    <div className="refinance-star-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="#0578EC"/>
                      </svg>
                    </div>
                    <p>
                      <strong>Current Mortgage Status:</strong>{' '}
                      Being up to date on your current mortgage improves your chances of approval.
                    </p>
                  </div>
                </div>
                <div className="refinance-qualify-ctas">
                  <button className="refinance-cta-primary">Get your rate</button>
                  <button className="refinance-cta-secondary">
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                      <path d="M6.28521 6.64001C6.80721 7.72723 7.5188 8.74621 8.41998 9.64739C9.32116 10.5486 10.3401 11.2602 11.4274 11.7822C11.5209 11.8271 11.5676 11.8495 11.6268 11.8668C11.8371 11.9281 12.0953 11.884 12.2733 11.7565C12.3234 11.7206 12.3663 11.6778 12.452 11.592C12.7143 11.3298 12.8454 11.1987 12.9772 11.113C13.4744 10.7898 14.1153 10.7898 14.6125 11.113C14.7443 11.1987 14.8754 11.3298 15.1376 11.592L15.2838 11.7382C15.6823 12.1368 15.8816 12.3361 15.9899 12.5501C16.2052 12.9758 16.2052 13.4785 15.9899 13.9041C15.8816 14.1182 15.6823 14.3175 15.2838 14.7161L15.1655 14.8343C14.7683 15.2315 14.5697 15.4301 14.2997 15.5818C14 15.7501 13.5347 15.8711 13.191 15.8701C12.8813 15.8692 12.6696 15.8091 12.2463 15.6889C9.97125 15.0432 7.82448 13.8249 6.0335 12.0339C4.24251 10.2429 3.02415 8.09612 2.37843 5.82108C2.25827 5.39774 2.19819 5.18607 2.19727 4.87636C2.19625 4.5327 2.31727 4.06733 2.48559 3.7677C2.63727 3.49768 2.83588 3.29907 3.2331 2.90185L3.35132 2.78362C3.74991 2.38504 3.9492 2.18575 4.16323 2.07749C4.5889 1.86219 5.0916 1.86219 5.51727 2.07749C5.73131 2.18575 5.9306 2.38504 6.32918 2.78362L6.47533 2.92978C6.73754 3.19198 6.86864 3.32308 6.95436 3.45492C7.2776 3.95208 7.2776 4.59302 6.95435 5.09019C6.86864 5.22202 6.73754 5.35312 6.47533 5.61533C6.3896 5.70106 6.34673 5.74393 6.31085 5.79404C6.18334 5.97211 6.13932 6.2303 6.20061 6.44057C6.21786 6.49974 6.24031 6.5465 6.28521 6.64001Z" stroke="black" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Contact us
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* When is the best time section */}
        <section className="refinance-best-time">
          <div className="refinance-container">
            <div className="refinance-best-time-content">
              <div className="refinance-best-time-text">
                <h2 className="refinance-section-title">When is the best time to refinance?</h2>
                <p className="refinance-best-time-description">
                  The best time to refinance is when interest rates are lower than your current rate, or when you want to take advantage of your home's increased value. You might also consider refinancing if your financial situation has improved or if you want to switch from an adjustable-rate mortgage (ARM) to a fixed-rate mortgage.
                </p>
                <div className="refinance-best-time-factors">
                  <p className="refinance-factors-title">Factors to Consider:</p>
                  <div className="refinance-benefits-list">
                    <div className="refinance-benefit-item">
                      <div className="refinance-star-icon">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="#0578EC"/>
                        </svg>
                      </div>
                      <p>
                        <strong>Current Interest Rates:</strong>{' '}
                        Are rates lower now than when you took out your mortgage?
                      </p>
                    </div>
                    <div className="refinance-benefit-item">
                      <div className="refinance-star-icon">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="#0578EC"/>
                        </svg>
                      </div>
                      <p>
                        <strong>Length of Time in Home:</strong>{' '}
                        Will you be in your home long enough to benefit from refinancing?
                      </p>
                    </div>
                    <div className="refinance-benefit-item">
                      <div className="refinance-star-icon">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="#0578EC"/>
                        </svg>
                      </div>
                      <p>
                        <strong>Your Financial Goals:</strong>{' '}
                        Are you looking to lower payments, shorten your loan term, or access equity?
                      </p>
                    </div>
                  </div>
                </div>
                <div className="refinance-best-time-ctas">
                  <button className="refinance-cta-primary">Get your rate</button>
                  <button className="refinance-cta-secondary">
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                      <path d="M6.28521 6.64001C6.80721 7.72723 7.5188 8.74621 8.41998 9.64739C9.32116 10.5486 10.3401 11.2602 11.4274 11.7822C11.5209 11.8271 11.5676 11.8495 11.6268 11.8668C11.8371 11.9281 12.0953 11.884 12.2733 11.7565C12.3234 11.7206 12.3663 11.6778 12.452 11.592C12.7143 11.3298 12.8454 11.1987 12.9772 11.113C13.4744 10.7898 14.1153 10.7898 14.6125 11.113C14.7443 11.1987 14.8754 11.3298 15.1376 11.592L15.2838 11.7382C15.6823 12.1368 15.8816 12.3361 15.9899 12.5501C16.2052 12.9758 16.2052 13.4785 15.9899 13.9041C15.8816 14.1182 15.6823 14.3175 15.2838 14.7161L15.1655 14.8343C14.7683 15.2315 14.5697 15.4301 14.2997 15.5818C14 15.7501 13.5347 15.8711 13.191 15.8701C12.8813 15.8692 12.6696 15.8091 12.2463 15.6889C9.97125 15.0432 7.82448 13.8249 6.0335 12.0339C4.24251 10.2429 3.02415 8.09612 2.37843 5.82108C2.25827 5.39774 2.19819 5.18607 2.19727 4.87636C2.19625 4.5327 2.31727 4.06733 2.48559 3.7677C2.63727 3.49768 2.83588 3.29907 3.2331 2.90185L3.35132 2.78362C3.74991 2.38504 3.9492 2.18575 4.16323 2.07749C4.5889 1.86219 5.0916 1.86219 5.51727 2.07749C5.73131 2.18575 5.9306 2.38504 6.32918 2.78362L6.47533 2.92978C6.73754 3.19198 6.86864 3.32308 6.95436 3.45492C7.2776 3.95208 7.2776 4.59302 6.95435 5.09019C6.86864 5.22202 6.73754 5.35312 6.47533 5.61533C6.3896 5.70106 6.34673 5.74393 6.31085 5.79404C6.18334 5.97211 6.13932 6.2303 6.20061 6.44057C6.21786 6.49974 6.24031 6.5465 6.28521 6.64001Z" stroke="black" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Contact us
                  </button>
                </div>
              </div>
              <div className="refinance-best-time-image">
                <img 
                  src={bestTimeImage || defaultPlaceholder} 
                  alt="Best time to refinance" 
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = defaultPlaceholder;
                  }}
                />
              </div>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="refinance-stats">
          <div className="refinance-stats-wrapper">
            <div 
              className="refinance-stats-image"
              style={{
                backgroundImage: statsBackgroundImage || undefined,
                backgroundSize: statsBackgroundImage ? 'cover' : undefined,
                backgroundPosition: statsBackgroundImage ? 'center' : undefined,
                backgroundRepeat: statsBackgroundImage ? 'no-repeat' : undefined
              }}
            >
              <div className="refinance-stats-image-fade"></div>
            </div>
            <div className="refinance-stats-content-wrapper">
              <div className="refinance-container">
                <div className="refinance-stats-content">
                  <div className="refinance-stats-text">
                    <h2 className="refinance-stats-title">We will find best rate</h2>
                    <div className="refinance-stats-ctas">
                      <button className="refinance-cta-primary">Get your rate</button>
                      <button className="refinance-cta-secondary">
                        <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                          <path d="M6.28521 6.64001C6.80721 7.72723 7.5188 8.74621 8.41998 9.64739C9.32116 10.5486 10.3401 11.2602 11.4274 11.7822C11.5209 11.8271 11.5676 11.8495 11.6268 11.8668C11.8371 11.9281 12.0953 11.884 12.2733 11.7565C12.3234 11.7206 12.3663 11.6778 12.452 11.592C12.7143 11.3298 12.8454 11.1987 12.9772 11.113C13.4744 10.7898 14.1153 10.7898 14.6125 11.113C14.7443 11.1987 14.8754 11.3298 15.1376 11.592L15.2838 11.7382C15.6823 12.1368 15.8816 12.3361 15.9899 12.5501C16.2052 12.9758 16.2052 13.4785 15.9899 13.9041C15.8816 14.1182 15.6823 14.3175 15.2838 14.7161L15.1655 14.8343C14.7683 15.2315 14.5697 15.4301 14.2997 15.5818C14 15.7501 13.5347 15.8711 13.191 15.8701C12.8813 15.8692 12.6696 15.8091 12.2463 15.6889C9.97125 15.0432 7.82448 13.8249 6.0335 12.0339C4.24251 10.2429 3.02415 8.09612 2.37843 5.82108C2.25827 5.39774 2.19819 5.18607 2.19727 4.87636C2.19625 4.5327 2.31727 4.06733 2.48559 3.7677C2.63727 3.49768 2.83588 3.29907 3.2331 2.90185L3.35132 2.78362C3.74991 2.38504 3.9492 2.18575 4.16323 2.07749C4.5889 1.86219 5.0916 1.86219 5.51727 2.07749C5.73131 2.18575 5.9306 2.38504 6.32918 2.78362L6.47533 2.92978C6.73754 3.19198 6.86864 3.32308 6.95436 3.45492C7.2776 3.95208 7.2776 4.59302 6.95435 5.09019C6.86864 5.22202 6.73754 5.35312 6.47533 5.61533C6.3896 5.70106 6.34673 5.74393 6.31085 5.79404C6.18334 5.97211 6.13932 6.2303 6.20061 6.44057C6.21786 6.49974 6.24031 6.5465 6.28521 6.64001Z" stroke="black" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        Contact us
                      </button>
                    </div>
                    <div className="refinance-stats-grid">
                      <div className="refinance-stat-item">
                        <div className="refinance-stat-number">17+</div>
                        <div className="refinance-stat-label">years in experience</div>
                      </div>
                      <div className="refinance-stat-item">
                        <div className="refinance-stat-number">128+</div>
                        <div className="refinance-stat-label">helped clients</div>
                      </div>
                      <div className="refinance-stat-item">
                        <div className="refinance-stat-number">96%</div>
                        <div className="refinance-stat-label">satisfaction rate</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        <section className="refinance-testimonials">
          <div className="refinance-container">
            <div className="refinance-testimonials-grid">
              <div className="refinance-testimonial">
                <div className="refinance-testimonial-divider">
                  <div className="refinance-testimonial-divider-line"></div>
                </div>
                <div className="refinance-testimonial-content">
                  <div className="refinance-testimonial-stars">
                    {[...Array(5)].map((_, i) => (
                      <svg key={i} width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M11.9996 17.8441L6.18281 20.9021L7.29371 14.4251L2.58786 9.838L9.09119 8.89301L11.9996 3L14.9079 8.89301L21.4113 9.838L16.7054 14.4251L17.8163 20.9021L11.9996 17.8441Z" fill="#0578EC"/>
                      </svg>
                    ))}
                  </div>
                  <p className="refinance-testimonial-author">
                    <strong>Elena Tuttle</strong> <span className="refinance-testimonial-location">from Tustin</span>
                  </p>
                  <p className="refinance-testimonial-text">
                    "Above and beyond. We had a very complex 1031 exchange and multiple mortgages with multiple entities. Stephan and his team worked tirelessly to ensure we closed on our properties"
                  </p>
                </div>
              </div>
              <div className="refinance-testimonial">
                <div className="refinance-testimonial-divider">
                  <div className="refinance-testimonial-divider-line"></div>
                </div>
                <div className="refinance-testimonial-content">
                  <div className="refinance-testimonial-stars">
                    {[...Array(5)].map((_, i) => (
                      <svg key={i} width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M11.9996 17.8441L6.18281 20.9021L7.29371 14.4251L2.58786 9.838L9.09119 8.89301L11.9996 3L14.9079 8.89301L21.4113 9.838L16.7054 14.4251L17.8163 20.9021L11.9996 17.8441Z" fill="#0578EC"/>
                      </svg>
                    ))}
                  </div>
                  <p className="refinance-testimonial-author">
                    <strong>Dan May</strong> <span className="refinance-testimonial-location">from Costa Mesa</span>
                  </p>
                  <p className="refinance-testimonial-text">
                    "My go to resource for everything mortgage related. Prompt service, valuable experience, competitive pricing.  Highly recommend.  You owe it to yourself and family to use The Best!"
                  </p>
                </div>
              </div>
              <div className="refinance-testimonial">
                <div className="refinance-testimonial-content">
                  <div className="refinance-testimonial-stars">
                    {[...Array(5)].map((_, i) => (
                      <svg key={i} width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M11.9996 17.8441L6.18281 20.9021L7.29371 14.4251L2.58786 9.838L9.09119 8.89301L11.9996 3L14.9079 8.89301L21.4113 9.838L16.7054 14.4251L17.8163 20.9021L11.9996 17.8441Z" fill="#0578EC"/>
                      </svg>
                    ))}
                  </div>
                  <p className="refinance-testimonial-author">
                    <strong>Agent Ivy</strong> <span className="refinance-testimonial-location">from Irvine</span>
                  </p>
                  <p className="refinance-testimonial-text">
                    "His strategic helped us procure a good loan at the best available rate. He took an interest in my concerns and my fears and offered me incredibly kind and helpful feedback."
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="refinance-faq">
          <div className="refinance-container">
            <div className="refinance-faq-content">
              <h2 className="refinance-faq-title">
                Have questions?<br />Find answer.
              </h2>
              <div className="refinance-faq-list">
                {faqs.map((faq, index) => (
                  <div key={index} className="refinance-faq-item">
                    <button 
                      className="refinance-faq-question"
                      onClick={() => toggleFAQ(index)}
                    >
                      <span>{faq.question}</span>
                      <svg 
                        className={`refinance-faq-icon ${openFAQ === index ? 'open' : ''}`}
                        width="24" 
                        height="24" 
                        viewBox="0 0 24 24" 
                        fill="none"
                      >
                        <path d="M18 15L12 9L6 15" stroke="#0578EC" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                    {openFAQ === index && (
                      <div className="refinance-faq-answer">
                        <p>{faq.answer}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div className="refinance-faq-contact">
                <div className="refinance-faq-contact-avatar">
                  <img 
                    src={contactAvatarImage || defaultPlaceholder} 
                    alt="Contact" 
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = defaultPlaceholder;
                    }}
                  />
                </div>
                <div className="refinance-faq-contact-info">
                  <h3 className="refinance-faq-contact-title">Still have a questions?</h3>
                  <div className="refinance-faq-contact-details">
                    <div className="refinance-faq-contact-item">
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path d="M10.0003 4.99996V9.99996L13.3337 11.6666M18.3337 9.99996C18.3337 14.6023 14.6027 18.3333 10.0003 18.3333C5.39795 18.3333 1.66699 14.6023 1.66699 9.99996C1.66699 5.39759 5.39795 1.66663 10.0003 1.66663C14.6027 1.66663 18.3337 5.39759 18.3337 9.99996Z" stroke="#0578EC" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <span>Mon - Fri, 9am to 7pm</span>
                    </div>
                    <div className="refinance-faq-contact-item">
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path d="M16.5625 3.75H3.4375C2.57417 3.75 1.875 4.45 1.875 5.3125V14.6875C1.875 15.55 2.575 16.25 3.4375 16.25H16.5625C17.425 16.25 18.125 15.55 18.125 14.6875V5.3125C18.125 4.44917 17.425 3.75 16.5625 3.75Z" stroke="#0578EC" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M4.375 6.25L10 10.625L15.625 6.25" stroke="#0578EC" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <span>Valentinoloanofficer@gmail.com</span>
                    </div>
                    <div className="refinance-faq-contact-item">
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path d="M17.4999 14.5376C16.8894 13.9222 15.4108 13.0246 14.6928 12.6627C13.7582 12.1917 13.6811 12.154 12.9467 12.6996C12.4569 13.064 12.1311 13.3889 11.5576 13.2666C10.9832 13.1444 9.73762 12.4543 8.64631 11.3671C7.555 10.2791 6.82555 8.99658 6.70329 8.42549C6.58021 7.85358 6.91089 7.53111 7.27192 7.04043C7.78065 6.34872 7.74209 6.23303 7.3072 5.29844C6.96832 4.57145 6.04358 3.10598 5.42654 2.49796C4.76601 1.84564 4.76601 1.96133 4.34016 2.13775C3.99345 2.28373 3.66084 2.46116 3.34649 2.66781C2.73109 3.07644 2.38975 3.41614 2.15098 3.92651C1.91138 4.43688 1.80471 5.63322 3.03797 7.87409C4.27123 10.115 5.13689 11.2604 6.9273 13.0467C8.71934 14.833 10.0962 15.7939 12.1098 16.9229C14.6009 18.3178 15.556 18.0462 16.068 17.8075C16.58 17.5695 16.9214 17.2306 17.3316 16.6152C17.5386 16.3013 17.716 15.969 17.8617 15.6224C18.0389 15.199 18.1546 15.199 17.5007 14.5385L17.4999 14.5376Z" stroke="#0578EC" strokeWidth="1.4"/>
                      </svg>
                      <span>+1 (714) 655-1681</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Other Mortgage Programs Section */}
        <section className="refinance-programs">
          <div className="refinance-container">
            <div className="refinance-programs-header">
              <h2 className="refinance-programs-title">Other mortgage programs</h2>
              <p className="refinance-programs-description">
                Explore our comprehensive mortgage solutions designed to meet your unique financial needs and goals.
              </p>
            </div>
            <div className="refinance-program-cards-grid">
              <div className="refinance-program-card">
                <div className="refinance-program-card-content">
                  <div className="refinance-program-icon">
                    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M16 2L20 12L30 14L22 22L24 32L16 26L8 32L10 22L2 14L12 12L16 2Z" fill="#0578EC"/>
                    </svg>
                  </div>
                  <h3 className="refinance-program-title">Purchase<br />New Home</h3>
                  <p className="refinance-program-description">
                    Get expert assistance to secure the best mortgage rates and terms, guiding you through the entire home-buying process.
                  </p>
                  <Link to="/buy-home" className="refinance-program-cta">Get your rate</Link>
                </div>
              </div>
              <div className="refinance-program-card highlighted">
                <div className="refinance-program-card-content">
                  <div className="refinance-program-icon">
                    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M16 2L20 12L30 14L22 22L24 32L16 26L8 32L10 22L2 14L12 12L16 2Z" fill="#FFFFFF"/>
                    </svg>
                  </div>
                  <h3 className="refinance-program-title">Refinance<br />Home</h3>
                  <p className="refinance-program-description">
                    Refinance your existing mortgage to lower interest rates, adjust loan terms, or access your home's equity quickly.
                  </p>
                  <Link to="/refinance-home" className="refinance-program-cta">Get your rate</Link>
                </div>
              </div>
              <div className="refinance-program-card">
                <div className="refinance-program-card-content">
                  <div className="refinance-program-icon">
                    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M16 2L20 12L30 14L22 22L24 32L16 26L8 32L10 22L2 14L12 12L16 2Z" fill="#0578EC"/>
                    </svg>
                  </div>
                  <h3 className="refinance-program-title">Reverse Mortgage</h3>
                  <p className="refinance-program-description">
                    A reverse mortgage allows homeowners 62+ to convert home equity into cash while staying in their property.
                  </p>
                  <Link to="/reverse-mortgage" className="refinance-program-cta">Get your rate</Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <RefinanceEligibilityForm 
        isOpen={showEligibilityForm}
        onClose={() => setShowEligibilityForm(false)}
        onSubmit={handleEligibilityFormSubmit}
      />
    </div>
  );
};

export default RefinanceHome;
